import React, { useContext, useEffect, useState } from 'react';
import { ACTIONS_COL_WIDTH, Alert, Button, Col, Form, Modal, Row, RowActions, StatusBadge as SharedStatusBadge, Table } from '@/components/ui';
import {
  BackdatedChoice,
  BackdatedPreview,
  ResourceRate,
  Vendor,
  VendorResource,
  vendorService,
} from '../../../services/vendorService';
import BackdatedRateChoices from './BackdatedRateChoices';
import { currency, shortDate } from '../utils/format';
import { isRateCurrent, rateRowStatus, upcomingLabel } from '../utils/rateDisplay';
import { AuthContext } from '../../../contexts/AuthContext';
import { notifyApprovalsChanged } from '../../../components/awaitingApprovalsCount';
import { confirmAction } from '@/components/ui';

interface Props {
  show: boolean;
  onHide: () => void;
  vendor: Vendor;
  resource?: VendorResource | null;
  onSaved: () => Promise<void> | void;
}

/** The date input wants yyyy-mm-dd, and must not shift the day while converting. */
const toDateInput = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/**
 * M19: what a row means TODAY, not just its workflow status. An approved rate that starts next
 * year is "Upcoming", and the superseded rate it replaces is still "In effect" until the day
 * before.
 */
const StatusBadge: React.FC<{ rate: ResourceRate }> = ({ rate }) => {
  // V2-032: the shared tint pill, on one line, instead of solid badges.
  switch (rateRowStatus(rate)) {
    case 'current':
      return <SharedStatusBadge status="in-effect" label="In effect" variant="success" />;
    case 'upcoming':
      return <SharedStatusBadge status="upcoming" label={`Upcoming – from ${shortDate(rate.effectiveFrom)}`} variant="primary" />;
    case 'pending':
      return (
        <SharedStatusBadge
          status="pending"
          label={rate.supersedesId ? 'Awaiting approval' : 'New rate – awaiting approval'}
          variant="warning"
        />
      );
    case 'rejected':
      return <SharedStatusBadge status="rejected" label="Rejected" variant="danger" />;
    default:
      return (
        <SharedStatusBadge
          status={rate.status}
          label={rate.status === 'SUPERSEDED' ? 'Superseded' : 'Ended'}
          variant="secondary"
        />
      );
  }
};

/**
 * Rates per service area (HE-M05) and the approval workflow (HE-M12).
 *
 * Every rate - a first rate as well as a change (H6) - is held until a different person
 * approves it on Vendor Approvals. The requester can withdraw their own pending request here
 * (M5); nobody can withdraw someone else's.
 *
 * The form sits behind an "Add Rate" button rather than always on screen — the same shape the
 * Other Costs tab in TaskModal uses, so the default view is the history and adding is a
 * deliberate act.
 *
 * "Edit" on an approved rate does NOT mutate it: it pre-fills the form so the change goes
 * through the normal supersede-then-approve path. Editing in place would silently rewrite what
 * work was actually charged at.
 */
const RateModal: React.FC<Props> = ({ show, onHide, vendor, resource, onSaved }) => {
  // Optional: the modal also renders in isolation (tests, previews) without an AuthProvider.
  const currentUser = useContext(AuthContext)?.currentUser;
  const [rates, setRates] = useState<ResourceRate[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [serviceAreaId, setServiceAreaId] = useState<string>('');
  const [rate, setRate] = useState<string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string>('');
  const [effectiveTo, setEffectiveTo] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** M4: the submissions a backdated rate would reprice, once asked for, and the choice for each. */
  const [backdated, setBackdated] = useState<BackdatedPreview | null>(null);
  const [backdatedChoices, setBackdatedChoices] = useState<Record<string, BackdatedChoice | undefined>>({});

  // Any change to what is being entered makes the list stale – ask again on the next submit.
  useEffect(() => {
    setBackdated(null);
    setBackdatedChoices({});
  }, [serviceAreaId, rate, effectiveFrom, effectiveTo, formOpen]);

  const load = async () => {
    if (!resource) return;
    try {
      setLoading(true);
      setRates(await vendorService.listRatesForResource(resource.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRate('');
    setComments('');
    setAgreementFile(null);
    setEffectiveFrom('');
    setEffectiveTo('');
    setServiceAreaId('');
  };

  useEffect(() => {
    if (!show || !resource) return;
    setError(null);
    setFormOpen(false);
    resetForm();
    load();
  }, [show, resource]);

  if (!resource) return null;

  // A rate can only exist where the resource is actually offered.
  const offeredAreas = (resource.serviceAreas ?? []).map((sa) => sa.serviceArea);

  // The rate this request would replace: the one in effect today in that area, else an
  // approved future one. None means this is a first rate (still needs approval - H6).
  const inArea = rates.filter((r) => String(r.serviceAreaId) === serviceAreaId);
  const currentRate = inArea.find((r) => isRateCurrent(r)) ?? null;
  const upcomingRate = inArea.find((r) => r.status === 'APPROVED' && !isRateCurrent(r)) ?? null;
  const isFirstRate = !currentRate && !upcomingRate;
  // Changing an existing rate needs a reason for the approver; a first rate's comment is optional.
  const willNeedApproval = !isFirstRate;

  const openAdd = () => {
    resetForm();
    setError(null);
    setFormOpen(true);
  };

  /** Pre-fill from an existing rate; the save path is unchanged. */
  const openEdit = (r: ResourceRate) => {
    setServiceAreaId(String(r.serviceAreaId));
    setRate(String(r.rate));
    setEffectiveFrom(toDateInput(r.effectiveFrom));
    setEffectiveTo(toDateInput(r.effectiveTo));
    setComments(r.comments ?? '');
    setAgreementFile(null);
    setError(null);
    setFormOpen(true);
  };

  const handleCancelPending = async (r: ResourceRate) => {
    if (!(await confirmAction({ title: 'Withdraw Rate Request?', body: 'It cannot be recovered.', confirmLabel: 'Withdraw', destructive: true }))) return;
    try {
      setError(null);
      await vendorService.deleteRate(r.id);
      notifyApprovalsChanged();
      await load();
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel rate change');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(rate);
    const trimmedComments = comments.trim();
    if (!serviceAreaId) {
      setError('Choose a service area.');
      return;
    }
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Enter a rate of zero or more.');
      return;
    }
    if (effectiveFrom && effectiveTo && effectiveTo < effectiveFrom) {
      setError('Effective To cannot be before Effective From.');
      return;
    }
    if (willNeedApproval && !trimmedComments) {
      setError('Explain why the rate is changing — the approver needs that context.');
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    const isBackdated = Boolean(effectiveFrom) && effectiveFrom < today;
    // M4: a rate starting in the past is never applied automatically. First show which Hired
    // Equipment submissions it would reprice, and let the coordinator choose for each.
    if (isBackdated && backdated == null) {
      try {
        setSaving(true);
        setError(null);
        const preview = await vendorService.previewBackdatedRate(resource.id, {
          serviceAreaId: parseInt(serviceAreaId, 10),
          rate: parsed,
          effectiveFrom,
          effectiveTo: effectiveTo || null,
        });
        if (preview.submissions.length > 0) {
          setBackdated(preview);
          setBackdatedChoices({});
          return;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not check which submissions this rate affects');
        return;
      } finally {
        setSaving(false);
      }
    }
    const pendingChoices = backdated?.submissions.filter((s) => !backdatedChoices[s.formId]) ?? [];
    if (pendingChoices.length > 0) {
      setError('Choose "Update to new rate" or "Leave as is" for every affected submission.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let agreementDocumentId: number | undefined;
      if (willNeedApproval && agreementFile) {
        const uploaded = await vendorService.addDocument(vendor.id, {
          docType: 'RATE_AGREEMENT',
          file: agreementFile,
        });
        agreementDocumentId = uploaded.id;
      }

      await vendorService.upsertRate(resource.id, {
        serviceAreaId: parseInt(serviceAreaId, 10),
        rate: parsed,
        effectiveFrom: effectiveFrom || undefined,
        effectiveTo: effectiveTo || null,
        comments: trimmedComments || undefined,
        agreementDocumentId,
        ...(backdated
          ? {
              backdatedChoices: backdated.submissions.map((s) => ({
                formId: s.formId,
                choice: backdatedChoices[s.formId] as BackdatedChoice,
              })),
            }
          : {}),
      });
      notifyApprovalsChanged();
      await load();
      resetForm();
      setFormOpen(false);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="2xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Rates – {resource.name} <span className="tw-text-content-muted">({vendor.name})</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {resource.isFixedPrice && (
          <Alert variant="info">
            This is a <strong>fixed-price</strong> resource: the rate below is charged as entered,
            with no markup applied.
          </Alert>
        )}

        <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
          <h6 className="tw-m-0">Rates by service area</h6>
          {!formOpen && (
            <Button variant="primary" size="sm" onClick={openAdd}>
              <i className="bi bi-plus-lg tw-mr-1" />
              Add Rate
            </Button>
          )}
        </div>

        {formOpen && (
          <Form onSubmit={handleSubmit} className="tw-mb-4 tw-p-3 tw-bg-surface-alt tw-rounded-lg">
            <Row>
              <Col md={3}>
                <Form.Group controlId="rate-service-area" className="tw-mb-3">
                  <Form.Label>Service Area *</Form.Label>
                  <Form.Select value={serviceAreaId} onChange={(e) => setServiceAreaId(e.target.value)}>
                    <option value="">Choose…</option>
                    {offeredAreas.map((sa) => (
                      <option key={sa.id} value={sa.id}>
                        {sa.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="rate-amount" className="tw-mb-3">
                  <Form.Label>Rate *</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="rate-effective-from" className="tw-mb-3">
                  <Form.Label>Effective From</Form.Label>
                  <Form.Control
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="rate-effective-to" className="tw-mb-3">
                  <Form.Label>Effective To</Form.Label>
                  <Form.Control
                    type="date"
                    value={effectiveTo}
                    min={effectiveFrom || undefined}
                    onChange={(e) => setEffectiveTo(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={willNeedApproval ? 8 : 12}>
                <Form.Group controlId="rate-comments" className="tw-mb-3">
                  <Form.Label>
                    Comments{willNeedApproval ? ' *' : ''}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={willNeedApproval ? 3 : 2}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder={
                      willNeedApproval
                        ? 'Why is this rate changing? Include context the approver needs (e.g. new agreement, fuel adjustment).'
                        : 'Optional – e.g. quote reference'
                    }
                  />
                </Form.Group>
              </Col>
              {willNeedApproval && (
                <Col md={4}>
                  <Form.Group controlId="rate-agreement" className="tw-mb-3">
                    <Form.Label>Updated agreement (optional)</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        setAgreementFile(input.files?.[0] ?? null);
                      }}
                    />
                    <Form.Text className="tw-text-content-muted">
                      Attach the updated subcontractor / HE agreement if you have one.
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>

            {serviceAreaId && (
              <Alert variant="warning" className="tw-mb-3">
                {isFirstRate ? (
                  <>
                    <strong>This first rate needs approval.</strong> It is held on Vendor Approvals
                    until a different person approves it, and the resource cannot be priced on a form
                    in this area until then.
                  </>
                ) : (
                  <>
                    <strong>This change needs approval.</strong>{' '}
                    {currentRate ? <>The rate in effect today is {currency(currentRate.rate)}. </> : null}
                    {upcomingRate ? <>An approved rate of {upcomingLabel(upcomingRate)} is already scheduled. </> : null}
                    It is held until a different person approves it, and pricing keeps using the
                    current rate until then. Once approved, the current rate ends the day before
                    the new one starts.
                  </>
                )}
              </Alert>
            )}

            {backdated && (
              <Alert variant="warning" className="tw-mb-3" data-testid="backdated-rate-panel">
                <p className="tw-mb-2">
                  <strong>This rate starts in the past.</strong> These Hired Equipment submissions have lines
                  for this resource dated from {shortDate(backdated.windowStart)} to {shortDate(backdated.windowEnd)}.
                  Choose for each whether it moves to the new rate or stays as it is (for example, already
                  invoiced to the vendor).
                </p>
                <BackdatedRateChoices
                  submissions={backdated.submissions}
                  choices={backdatedChoices}
                  disabled={saving}
                  onChange={(formId, choice) => setBackdatedChoices((prev) => ({ ...prev, [formId]: choice }))}
                />
                <p className="tw-mb-0 tw-mt-2 tw-text-xs">
                  Nothing changes now. Your choices go to the Sourcing Manager with the rate change and are
                  applied only if it is approved. An Approved submission that is updated stays Approved.
                </p>
              </Alert>
            )}

            <div className="tw-flex tw-justify-end tw-gap-2">
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => {
                  setFormOpen(false);
                  resetForm();
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Submit for Approval'}
              </Button>
            </div>
          </Form>
        )}

        <Table responsive className="tw-mb-0">
          <thead>
            <tr>
              <th>Service Area</th>
              <th>Rate</th>
              <th>Effective From</th>
              <th>Effective To</th>
              <th>Status</th>
              <th>Requested By</th>
              <th>Reason</th>
              <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="tw-text-center tw-py-3">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rates.length === 0 && (
              <tr>
                <td colSpan={8} className="tw-text-center tw-py-4 tw-text-content-muted">
                  <i className="bi bi-cash-stack tw-text-3xl tw-block tw-mb-2" />
                  No rates set for this resource yet.
                  <div>
                    <small>Use &ldquo;Add Rate&rdquo; to set one per service area.</small>
                  </div>
                </td>
              </tr>
            )}
            {!loading &&
              rates.map((r) => (
                <tr key={r.id}>
                  <td>{r.serviceArea?.name ?? r.serviceAreaId}</td>
                  <td>{currency(r.rate)}</td>
                  <td>{shortDate(r.effectiveFrom)}</td>
                  <td>{r.effectiveTo ? shortDate(r.effectiveTo) : '–'}</td>
                  <td>
                    <StatusBadge rate={r} />
                  </td>
                  <td>{r.requestedBy ? `${r.requestedBy.name} ${r.requestedBy.lastName}` : '–'}</td>
                  <td>
                    {r.comments ? (
                      <span title={r.comments} className="tw-line-clamp-2">
                        {r.comments}
                      </span>
                    ) : (
                      '–'
                    )}
                    {r.agreementDocument && (
                      <div className="tw-mt-1">
                        <a
                          href={vendorService.getDocumentDownloadUrl(r.agreementDocument.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tw-text-sm"
                        >
                          <i className="bi bi-paperclip tw-mr-1" />
                          {r.agreementDocument.fileName}
                        </a>
                      </div>
                    )}
                  </td>
                  <td className="tw-text-right">
                    {/* Superseded and rejected rows are history; there is nothing to act on. */}
                    <RowActions
                      actions={[
                        {
                          kind: 'edit',
                          label: 'Change Rate',
                          onClick: () => openEdit(r),
                          // A pending request is withdrawn and re-entered, not changed in place.
                          hidden: !(r.status === 'APPROVED' || isRateCurrent(r)),
                        },
                        {
                          kind: 'cancel',
                          label: 'Withdraw Request',
                          onClick: () => handleCancelPending(r),
                          // M5: only the person who asked for it can withdraw it.
                          hidden: r.status !== 'PENDING' || !currentUser?.id || r.requestedById !== currentUser.id,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>

        <div className="tw-text-sm tw-text-content-muted tw-mt-3">
          Editing an approved rate does not overwrite it – it submits a change that supersedes it
          once approved, so what the work was charged at stays on the record. Dates are whole days:
          a rate applies from the start of its first day to the end of its last.
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" type="button" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RateModal;
